import { useEffect, useState } from 'react';

import AJAX from 'common/AJAX';
import parseAPIResponse from 'common/util/parseAPIResponse';

import type { Option } from 'common/ui/common/select/SelectCommon';

type Group = {
  id: string;
  name: string;
};

type GetGroupsResponse = {
  groups: Group[];
};

export const useZendeskSourceFilter = (selectedGroupIDs: string[] | null) => {
  const [groups, setGroups] = useState<Group[] | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let ignoreResult = false;

    // If not loading, we're already fetched the groups, don't do it again
    if (!loading) {
      return;
    }

    const fetchGroups = async () => {
      const response = await AJAX.post('/api/zendesk/getGroups', {});
      const { error, parsedResponse } = parseAPIResponse<GetGroupsResponse>(response, {
        isSuccessful: (parsedResponse) => {
          return typeof parsedResponse.groups === 'object' && Array.isArray(parsedResponse.groups);
        },
      });
      if (ignoreResult) {
        return;
      }

      if (error || !parsedResponse) {
        setError(true);
      } else {
        const { groups } = parsedResponse;
        setGroups(groups);
      }
      setLoading(false);
    };
    fetchGroups();

    return () => {
      ignoreResult = true;
    };
  }, [loading]);

  const options =
    groups?.map((group) => ({
      label: group.name,
      value: group.id,
    })) ?? [];

  const selectedOptions = groups
    ?.map((group) => {
      const isSelected = selectedGroupIDs?.includes(group.id);
      return isSelected ? { label: group.name, value: group.id } : null;
    })
    .filter(Boolean) as Option[];

  return {
    loading,
    error,
    options,
    selectedOptions,
  };
};
